import { BACKEND_URL } from '../config/api';
import React, { useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';

/**
 * SubscribeForm Component
 * 
 * Public-facing email subscription form for the HopSeat mailing list.
 * 
 * Features:
 * - Email validation
 * - Success/error feedback
 * - Loading states
 * - Responsive design
 * - Dark theme styling
 * 
 * Usage:
 * <SubscribeForm />
 */
const SubscribeForm = () => {
  // Get backend URL from environment
  const API_URL = BACKEND_URL;
  
  if (!API_URL) {
    throw new Error('REACT_APP_BACKEND_URL environment variable is required');
  }
  
  // Component state
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  /**
   * Validate email format
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(false);
    
    // Validate email
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // POST to subscribe endpoint
      const response = await axios.post(
        `${API_URL}/api/subscribe`,
        { email: email.trim() }
      );
      
      // Success
      if (response.data.success) {
        setSuccess(true);
        setEmail(''); // Clear input
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      }
      
    } catch (error) {
      console.error('Subscription error:', error);
      
      // Handle error responses
      if (error.response && error.response.data) {
        setError(error.response.data.detail || 'Subscription failed');
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="subscribe-form-container" data-testid="landing-subscribe-form-container">
      <div className="subscribe-form-card" data-testid="landing-subscribe-form-card">
        
        {/* Header */}
        <div className="form-header" data-testid="landing-subscribe-form-header">
          <Mail size={32} className="form-icon" />
          <h3 className="form-title" data-testid="landing-subscribe-form-title">Stay Updated</h3>
          <p className="form-description" data-testid="landing-subscribe-form-description">
            Subscribe to our newsletter for the latest updates, features, and industry news.
          </p>
        </div>
        
        {/* Success Message */}
        {success && (
          <div className="alert alert-success" data-testid="landing-subscribe-success-alert">
            <CheckCircle size={20} />
            <div className="alert-content">
              <strong>Success!</strong>
              <p>You've been added to our mailing list. Check your inbox!</p>
            </div>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="alert alert-error" data-testid="landing-subscribe-error-alert">
            <AlertCircle size={20} />
            <div className="alert-content">
              <strong>Error</strong>
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {/* Subscription Form */}
        <form onSubmit={handleSubmit} className="subscribe-form" data-testid="landing-subscribe-form">
          <div className="form-group">
            <div className="input-wrapper" data-testid="landing-subscribe-email-input-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="email-input"
                disabled={isSubmitting}
                aria-label="Email address"
                data-testid="landing-subscribe-email-input"
              />
            </div>
          </div>
          
          <button
            type="submit"
            className="subscribe-button"
            disabled={isSubmitting || success}
            data-testid="landing-subscribe-submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader size={20} className="spinner" />
                Subscribing...
              </>
            ) : success ? (
              <>
                <CheckCircle size={20} />
                Subscribed!
              </>
            ) : (
              <>
                <Mail size={20} />
                Subscribe
              </>
            )}
          </button>
        </form>
        
        {/* Privacy Notice */}
        <p className="privacy-notice" data-testid="landing-subscribe-privacy-note">
          We respect your privacy. Unsubscribe at any time.
        </p>
        
      </div>
      
      <style jsx>{`
        .subscribe-form-container {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        
        .subscribe-form-card {
          background: linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%);
          border: 2px solid #333333;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          max-width: 600px;
          margin: 0 auto;
        }
        
        .form-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .form-icon {
          color: #ff4500;
          margin-bottom: 1rem;
        }
        
        .form-title {
          color: #ffffff;
          font-size: 1.75rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
          font-family: 'Inter', sans-serif;
        }
        
        .form-description {
          color: #cccccc;
          font-size: 1rem;
          margin: 0;
          line-height: 1.6;
        }
        
        .alert {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          animation: slideIn 0.3s ease;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .alert-success {
          background: rgba(34, 197, 94, 0.1);
          border: 2px solid #22c55e;
          color: #22c55e;
        }
        
        .alert-error {
          background: rgba(239, 68, 68, 0.1);
          border: 2px solid #ef4444;
          color: #ef4444;
        }
        
        .alert-content strong {
          display: block;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }
        
        .alert-content p {
          margin: 0;
          font-size: 0.95rem;
        }
        
        .subscribe-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .form-group {
          width: 100%;
        }
        
        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .input-icon {
          position: absolute;
          left: 1rem;
          width: 18px;
          height: 18px;
          color: #999999;
          pointer-events: none;
          z-index: 1;
        }
        
        .email-input {
          width: 100%;
          min-height: 54px;
          padding: 1rem 1rem 1rem 3rem;
          background: #0f0f0f;
          border: 2px solid #333333;
          border-radius: 12px;
          color: #ffffff;
          font-size: 1rem;
          line-height: 1.3;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }
        
        .email-input:focus {
          outline: none;
          border-color: #ff4500;
          box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.1);
        }
        
        .email-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .email-input::placeholder {
          color: #666666;
        }
        
        .subscribe-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #ff4500;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .subscribe-button:hover:not(:disabled) {
          background: #e03e00;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 69, 0, 0.4);
        }
        
        .subscribe-button:active:not(:disabled) {
          transform: translateY(0);
        }
        
        .subscribe-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .spinner {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .privacy-notice {
          text-align: center;
          color: #999999;
          font-size: 0.875rem;
          margin: 1.5rem 0 0 0;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .subscribe-form-card {
            padding: 1.5rem;
          }
          
          .form-title {
            font-size: 1.5rem;
          }
          
          .form-description {
            font-size: 0.95rem;
          }
        }
        
        @media (max-width: 480px) {
          .subscribe-form-card {
            padding: 1.25rem;
            border-radius: 12px;
          }
          
          .form-title {
            font-size: 1.25rem;
          }
          
          .email-input {
            min-height: 50px;
            padding: 0.875rem 0.875rem 0.875rem 3.35rem;
            font-size: 0.95rem;
          }

          .input-icon {
            left: 0.95rem;
            width: 18px;
            height: 18px;
          }

          .subscribe-button {
            padding: 0.875rem;
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SubscribeForm;
